import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type Language = "en" | "ar";

interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (en: string, ar?: string | null) => string;
  isRTL: boolean;
  /** Generates the correct path for a given route in the target language */
  localePath: (path: string, targetLang?: Language) => string;
  /** Switch language and navigate to the equivalent page */
  switchLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}

function detectBrowserLanguage(): Language {
  const saved = localStorage.getItem("wavai-lang") as Language | null;
  if (saved === "ar" || saved === "en") return saved;
  const browserLang = navigator.language || (navigator as any).userLanguage || "";
  return browserLang.startsWith("ar") ? "ar" : "en";
}

function getLanguageFromPath(pathname: string): Language {
  return pathname.startsWith("/ar") ? "ar" : "en";
}

/** Strip /ar prefix to get the canonical English path */
function stripArPrefix(pathname: string): string {
  if (pathname === "/ar") return "/";
  if (pathname.startsWith("/ar/")) return pathname.slice(3);
  return pathname;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const pathLang = getLanguageFromPath(location.pathname);
  const [lang, setLangState] = useState<Language>(pathLang);

  // Sync lang with URL on navigation
  useEffect(() => {
    const newLang = getLanguageFromPath(location.pathname);
    if (newLang !== lang) {
      setLangState(newLang);
    }
  }, [location.pathname]);

  // Update HTML dir and lang attributes + localStorage
  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    localStorage.setItem("wavai-lang", lang);
  }, [lang]);

  // On first load, if no /ar prefix but browser detects Arabic, redirect
  useEffect(() => {
    const browserLang = detectBrowserLanguage();
    if (browserLang === "ar" && !location.pathname.startsWith("/ar") && !location.pathname.startsWith("/admin")) {
      // Don't auto-redirect, just let user choose
    }
  }, []);

  const setLang = (newLang: Language) => {
    const canonicalPath = stripArPrefix(location.pathname);
    const targetPath = newLang === "ar" 
      ? (canonicalPath === "/" ? "/ar" : `/ar${canonicalPath}`)
      : canonicalPath;
    setLangState(newLang);
    navigate(targetPath + location.search + location.hash, { replace: true });
  };

  const switchLanguage = () => {
    setLang(lang === "en" ? "ar" : "en");
  };

  const t = (en: string, ar?: string | null): string => {
    if (lang === "ar" && ar) return ar;
    return en;
  };

  const localePath = (path: string, targetLang?: Language): string => {
    const l = targetLang || lang;
    const clean = stripArPrefix(path);
    if (l === "ar") return clean === "/" ? "/ar" : `/ar${clean}`;
    return clean;
  };

  const isRTL = lang === "ar";

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL, localePath, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
